/**
 * WMS PM Check Sheet API Service
 * Handles all API calls for WMS PM Check Sheet operations
 */

import backendApi from '../../../services/backendApi';

export interface ChecklistItem {
  id?: number;
  serial_number?: number;
  description: string;
  status_ok: boolean;
  status_not_ok: boolean;
  corrective_action?: string;
}

export interface WMSPMCheckSheetData {
  id?: number;
  pm_date: string;
  make?: string;
  location?: string;
  rating?: string;
  frequency: string;
  remarks?: string;
  engineer?: string;
  tech?: string;
  site_incharge_name?: string;
  signature_image?: string;
  signature_type?: 'draw' | 'upload';
  checklist_items: ChecklistItem[];
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

class WMSPMCheckSheetApi {
  private baseEndpoint = '/v1/wms-pm-check-sheet';

  /**
   * Get all check sheets for the current site
   */
  async getAll(siteCode?: string): Promise<ApiResponse<WMSPMCheckSheetData[]>> {
    try {
      const headers: Record<string, string> = {};
      if (siteCode) {
        headers['X-Site-Code'] = siteCode;
      }

      const response = await backendApi.makeRequest(this.baseEndpoint + '/', {
        method: 'GET',
        headers,
      });

      return {
        success: true,
        data: Array.isArray(response) ? response : response.results || [],
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch check sheets',
      };
    }
  }

  /**
   * Get a single check sheet by ID
   */
  async getById(id: number, siteCode?: string): Promise<ApiResponse<WMSPMCheckSheetData>> {
    try {
      const headers: Record<string, string> = {};
      if (siteCode) {
        headers['X-Site-Code'] = siteCode;
      }

      const response = await backendApi.makeRequest(`${this.baseEndpoint}/${id}/`, {
        method: 'GET',
        headers,
      });

      return {
        success: true,
        data: response,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch check sheet',
      };
    }
  }

  /**
   * Create a new check sheet
   */
  async create(data: WMSPMCheckSheetData, siteCode?: string): Promise<ApiResponse<WMSPMCheckSheetData>> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (siteCode) {
        headers['X-Site-Code'] = siteCode;
      }

      // Get current user ID to ensure user_id is set
      let userId: number | null = null;
      try {
        const userResponse = await backendApi.getCurrentUser();
        if (userResponse.success && userResponse.user && userResponse.user.id) {
          userId = userResponse.user.id;
        }
      } catch (error) {
}

      // Prepare payload
      const payload: any = {
        pm_date: data.pm_date,
        make: data.make || '',
        location: data.location || '',
        rating: data.rating || '',
        frequency: data.frequency || 'Weekly',
        remarks: data.remarks || '',
        engineer: data.engineer || '',
        tech: data.tech || '',
        site_incharge_name: data.site_incharge_name || '',
        checklist_items: data.checklist_items.map((item, index) => ({
          serial_number: item.serial_number || index + 1,
          description: item.description,
          status_ok: item.status_ok,
          status_not_ok: item.status_not_ok,
          corrective_action: item.corrective_action || '',
        })),
      };

      // Add user_id if available
      if (userId) {
        payload.user_id = userId;
      }

      // Add signature if provided
      if (data.signature_image) {
        payload.signature_image = data.signature_image;
        payload.signature_type = data.signature_type || 'draw';
      }

      const response = await backendApi.makeRequest(this.baseEndpoint + '/', {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      return {
        success: true,
        data: response,
        message: 'Check sheet created successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to create check sheet',
      };
    }
  }

  /**
   * Update an existing check sheet
   */
  async update(
    id: number,
    data: Partial<WMSPMCheckSheetData>,
    siteCode?: string
  ): Promise<ApiResponse<WMSPMCheckSheetData>> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (siteCode) {
        headers['X-Site-Code'] = siteCode;
      }

      // Get current user ID to ensure user_id is set
      let userId: number | null = null;
      try {
        const userResponse = await backendApi.getCurrentUser();
        if (userResponse.success && userResponse.user && userResponse.user.id) {
          userId = userResponse.user.id;
        }
      } catch (error) {
}

      // Prepare payload
      const payload: any = {};
      if (data.pm_date) payload.pm_date = data.pm_date;
      if (data.make !== undefined) payload.make = data.make;
      if (data.location !== undefined) payload.location = data.location;
      if (data.rating !== undefined) payload.rating = data.rating;
      if (data.frequency) payload.frequency = data.frequency;
      if (data.remarks !== undefined) payload.remarks = data.remarks;
      if (data.engineer !== undefined) payload.engineer = data.engineer;
      if (data.tech !== undefined) payload.tech = data.tech;
      if (data.site_incharge_name !== undefined) payload.site_incharge_name = data.site_incharge_name;
      
      // Add user_id if available
      if (userId) {
        payload.user_id = userId;
      }
      
      if (data.signature_image !== undefined) {
        payload.signature_image = data.signature_image;
        payload.signature_type = data.signature_type || 'draw';
      }

      // Include checklist items if provided
      if (data.checklist_items) {
        payload.checklist_items = data.checklist_items.map((item, index) => ({
          serial_number: item.serial_number || index + 1,
          description: item.description,
          status_ok: item.status_ok,
          status_not_ok: item.status_not_ok,
          corrective_action: item.corrective_action || '',
        }));
      }

      const response = await backendApi.makeRequest(`${this.baseEndpoint}/${id}/`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
      });

      return {
        success: true,
        data: response,
        message: 'Check sheet updated successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to update check sheet',
      };
    }
  }

  /**
   * Update only the signature
   */
  async updateSignature(
    id: number,
    signatureImage: string,
    signatureType: 'draw' | 'upload',
    siteCode?: string
  ): Promise<ApiResponse<WMSPMCheckSheetData>> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (siteCode) {
        headers['X-Site-Code'] = siteCode;
      }

      const response = await backendApi.makeRequest(`${this.baseEndpoint}/${id}/`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({
          signature_image: signatureImage,
          signature_type: signatureType,
        }),
      });

      return {
        success: true,
        data: response,
        message: 'Signature updated successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to update signature',
      };
    }
  }

  /**
   * Delete a check sheet
   */
  async delete(id: number, siteCode?: string): Promise<ApiResponse<void>> {
    try {
      const headers: Record<string, string> = {};
      if (siteCode) {
        headers['X-Site-Code'] = siteCode;
      }

      await backendApi.makeRequest(`${this.baseEndpoint}/${id}/`, {
        method: 'DELETE',
        headers,
      });

      return {
        success: true,
        message: 'Check sheet deleted successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to delete check sheet',
      };
    }
  }
}

export default new WMSPMCheckSheetApi();


